import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RiCloseLine, RiSaveLine, RiMailLine, RiMapPinLine } from '@remixicon/react';
import { apiClient } from '@/lib/api';
import PhoneInput from '@/components/forms/PhoneInput';

const EditClientModal = ({ client, onClose, onSaved }) => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    countryCode: '+1',
    email: '',
    address1: '',
    city: '',
    state: '',
    zip_code: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (client) {
      const phone = client.contact_numbers?.[0] || client.phone || '';
      setForm({
        firstName: client.first_name || '',
        lastName: client.last_name || '',
        phone,
        countryCode: '+1',
        email: client.email || '',
        address1: client.address1 || '',
        city: client.city || '',
        state: client.state || '',
        zip_code: client.zip_code || '',
      });
    }
  }, [client]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email';
    if (!form.phone.trim()) newErrors.phone = 'Phone is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const clientId = client.client_id || client.id;
      const payload = {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        contact_numbers: form.phone ? [`${form.countryCode}${form.phone}`] : [],
        address1: form.address1.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zip_code: form.zip_code.trim() || null,
      };
      return apiClient.updateClient(clientId, payload);
    },
    onSuccess: (updatedClient) => {
      toast.success('Client updated successfully!');
      // Merge updated fields back so the parent gets fresh data
      const merged = {
        ...client,
        ...updatedClient,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        contact_numbers: form.phone ? [`${form.countryCode}${form.phone}`] : [],
        phone: form.phone ? `${form.countryCode}${form.phone}` : '',
        address1: form.address1.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zip_code: form.zip_code.trim() || null,
      };
      onSaved?.(merged);
    },
    onError: (err) => {
      console.error('Failed to update client:', err);
      toast.error('Failed to update client. Please try again.');
    },
  });

  const handleSubmit = () => {
    if (validate()) {
      saveMutation.mutate();
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
      errors[field] ? 'border-red-500' : 'border-gray-200'
    }`;

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/30 z-[9998]" onClick={onClose} />
      <div className="fixed z-[9999] inset-x-4 top-1/2 -translate-y-1/2 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-md bg-white rounded-2xl shadow-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Edit Client</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* First Name & Last Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                First Name<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                placeholder="First name..."
                className={inputClass('firstName')}
              />
              {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Last Name<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                placeholder="Last name..."
                className={inputClass('lastName')}
              />
              {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
            </div>
          </div>

          {/* Phone */}
          <PhoneInput
            countryCode={form.countryCode}
            phone={form.phone}
            onCountryCodeChange={(code) => updateField('countryCode', code)}
            onPhoneChange={(phone) => updateField('phone', phone)}
            error={errors.phone}
            required
          />

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Email<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <RiMailLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="example@email.com"
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.email ? 'border-red-500' : 'border-gray-200'
                }`}
              />
            </div>
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Address<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <RiMapPinLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={form.address1}
                onChange={(e) => updateField('address1', e.target.value)}
                placeholder="123 Main Street, Apt 4B"
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.address1 ? 'border-red-500' : 'border-gray-200'
                }`}
              />
            </div>
            {errors.address1 && <p className="text-xs text-red-500 mt-1">{errors.address1}</p>}
          </div>

          {/* City, State, Zip */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                City<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => updateField('city', e.target.value)}
                placeholder="New York"
                className={inputClass('city')}
              />
              {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                State<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.state}
                onChange={(e) => updateField('state', e.target.value)}
                placeholder="NY"
                className={inputClass('state')}
              />
              {errors.state && <p className="text-xs text-red-500 mt-1">{errors.state}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Zip Code<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.zip_code}
                onChange={(e) => updateField('zip_code', e.target.value)}
                placeholder="10001"
                className={inputClass('zip_code')}
              />
              {errors.zip_code && <p className="text-xs text-red-500 mt-1">{errors.zip_code}</p>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-primary-600 bg-primary-50/90 hover:bg-white hover:border-primary-700 border border-transparent rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RiSaveLine className="w-4 h-4" />
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default EditClientModal;